import { useEffect, useMemo, useState } from 'react';
import {
  Autocomplete,
  Chip,
  FormControlLabel,
  Stack,
  Switch,
  TextField,
  TextFieldProps,
  Typography,
  useTheme,
} from '@mui/material';
import { isEmpty, last } from 'lodash';

// Components
import { IsExportType, StyledWhiteBox } from 'src/features/PortDetail/config';
import { renderTextOrDefault } from 'src/utils/helpers/stringHelpers';
import { LIMIT_GET_LIST } from 'src/utils/constants/config';
import Table from 'src/components/Table';

// Utilities
import { ILeadTimesNew, IPartnerTradeTransit } from 'src/utils/typings/response';
import { getArrayDefault } from 'src/utils/helpers/arrayHelpers';
import { useQueryDataPartnerTradeTransit, useQueryDataLeadTimesNew } from 'src/services/queries';
import PaperSelect from 'src/components/common/PaperSelect';
import IconFeather from 'src/components/common/Icon';
import { Search } from 'react-feather';
import TransitTradeChart from 'src/features/PortDetail/TradeTransit/TransitTradeChart';

interface IProps {
  locode: string;
}

const TransitTradeTable = ({ locode }: IProps) => {
  const theme = useTheme();
  const [isExport, setIsExport] = useState<boolean>(false);
  const [partner, setPartner] = useState<Array<IPartnerTradeTransit>>([]);
  const { data } = useQueryDataPartnerTradeTransit({ page: 1, pageSize: LIMIT_GET_LIST });
  const { data: dataLeadTime, isFetching: isFetchingLeadTime } = useQueryDataLeadTimesNew({
    locode,
    locodePartner: partner?.[0]?.value || '',
    isExport: isExport ? IsExportType.Export : IsExportType.Export,
  });
  const listdataLeadTimeNew = getArrayDefault(dataLeadTime?.data);

  const listPartners = useMemo(
    () => getArrayDefault(data?.data).filter((trade) => trade?.value !== locode),
    [data?.data],
  );

  useEffect(() => {
    if (!isEmpty(listPartners)) {
      setPartner([listPartners[0]]);
    }
  }, [listPartners]);

  const configColumn: ConfigCell<ILeadTimesNew>[] = [
    {
      id: 'operator',
      disablePadding: true,
      disableSort: true,
      label: 'Operator',
      cell: (row: ILeadTimesNew) => {
        return (
          <Typography variant="body2" color="text.secondary">
            {renderTextOrDefault(row?.operator)}
          </Typography>
        );
      },
    },
    {
      id: 'days_to_partner',
      disablePadding: true,
      disableSort: true,
      label: `Days from ${partner?.[0]?.name}`,
      cell: (row: ILeadTimesNew) => {
        return (
          <Typography variant="body2" color="text.secondary">
            {renderTextOrDefault(row?.days_to_partner)}
          </Typography>
        );
      },
    },
  ];

  return (
    <StyledWhiteBox display="flex" flexDirection="column" gap={2}>
      <Typography variant="h5" align="left">
        Transit times & Trade
      </Typography>
      <Stack direction="row" spacing={5}>
        <FormControlLabel
          control={
            <Switch
              checked={isExport}
              onChange={(e) => {
                setIsExport(e.target.checked);
              }}
              size="medium"
              color="primary"
              id="by_port_country_import"
              name="by_port_country_import"
            />
          }
          sx={{
            '& .MuiFormControlLabel-label': {
              marginLeft: theme.spacing(1),
            },
          }}
          label={isExport ? 'Exports view' : 'Imports view'}
        />
        <Autocomplete
          value={partner}
          onChange={(_, value) => {
            setPartner([last(getArrayDefault(value))]);
          }}
          clearIcon={false}
          autoComplete
          options={listPartners}
          popupIcon={<IconFeather muiProps={{ fontSize: 'small' }} icon={<Search />} />}
          PaperComponent={PaperSelect}
          multiple
          getOptionLabel={(option) => option.name || ''}
          renderTags={(value) =>
            value.map((option: IPartnerTradeTransit, index) => (
              <Chip color="secondary" id={option?.value || ''} key={`partner-${index}`} label={option?.name} />
            ))
          }
          renderInput={(params: TextFieldProps) => (
            <TextField
              {...params}
              InputLabelProps={{
                shrink: true,
              }}
              required
              disabled
              size="small"
              fullWidth
              label="Partner"
            />
          )}
        />
      </Stack>
      <Stack mb={3} spacing={3} direction={'row'} alignItems="flex-start" flex={1}>
        <Stack flex={1}>
          <Table
            data={listdataLeadTimeNew}
            configColumn={configColumn}
            loading={isFetchingLeadTime}
            dataKey="operator"
            isPagination={false}
            maxRow={10}
            sxTableContainer={{
              border: `1px solid ${theme.palette.divider}`,
            }}
          />
        </Stack>
        <Stack flex={1}>
          <TransitTradeChart
            locode={locode}
            partner={partner[0]}
            isExport={isExport ? IsExportType.Export : IsExportType.Export}
          />
        </Stack>
      </Stack>
    </StyledWhiteBox>
  );
};

export default TransitTradeTable;
